import {
	type DismissCallback,
	useMessagePlacementHooks,
} from '@atlassian/post-office-context/message-placement';

import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';

import type { ErrorBoundariedProps } from '@post-office/placement-contexts/create-placement-component/types';

import { useInitialData } from '@post-office/placement-contexts/hooks';

import { type ComponentType, useEffect, useState } from 'react';

import { type Message } from './message-renderer';

import { initializeMessageRenderer } from '../__generated__/client/render';

import { PLACEMENT_ID } from '../__generated__/common';

import { withHomeRecommendedActionIntlProvider } from '../__generated__/intl-provider';

import { type HomeRecommendedActionInitialData } from '../api/types';

import { type Placement as HomeRecommendedActionPlacement } from '../contracts/Placement';

// Jira Local Consumption - MessageRenderer is typed as 'React.FC<any>' as otherwise it fails typecheck within Jira with the below error:
// The inferred type of 'MessageRenderer' cannot be named without a reference to '@post-office/placement-common/node_modules/react-error-boundary'. This is likely not portable. A type annotation is necessary.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Type '(props: any) => ReactNode' is not assignable to type 'FC<any>'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MessageRenderer: React.FC<any> = initializeMessageRenderer();

type HomeRecommendedActionPlacementProps = {
	initialData: Promise<Response | undefined> | undefined;
	extraAnalyticsAttributes?: Record<string, string>;
};

export const HomeRecommendedActionPlacementComponent: ComponentType<
	ErrorBoundariedProps<HomeRecommendedActionPlacementProps>
> = withHomeRecommendedActionIntlProvider(
	createPlacementComponent<HomeRecommendedActionPlacement>(
		PLACEMENT_ID,
		({ initialData, extraAnalyticsAttributes }: HomeRecommendedActionPlacementProps) => {
			const initialDataState = useInitialData<HomeRecommendedActionInitialData>(initialData);

			const [localMessageState, setLocalMessageState] = useState<Message[]>([]);

			useEffect(() => {
				if (initialDataState?.data?.messages?.length) {
					setLocalMessageState(initialDataState.data.messages as Message[]);
				}
			}, [initialDataState?.data?.messages]);

			const { onMessageDismiss, onMessageClicked, onMessageSnooze } = useMessagePlacementHooks();

			const handleDismiss: DismissCallback = ({ messageInstanceId }) => {
				setLocalMessageState((current) =>
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
					current.filter((e) => e.messageInstanceId !== messageInstanceId),
				);
			};

			useEffect(() => onMessageDismiss(handleDismiss), [onMessageDismiss]);
			useEffect(() => onMessageClicked(handleDismiss), [onMessageClicked]);
			useEffect(() => onMessageSnooze(handleDismiss), [onMessageSnooze]);

			if (initialDataState.error) {
				return null;
			}

			if (initialDataState.isLoading) {
				// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
				return <p data-testid="home-recommended-action">Loading</p>;
			}

			return (
				<div>
					{localMessageState.map((message) => (
						<MessageRenderer
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
							key={message.messageInstanceId}
							{...message}
							loadingFallback={
								// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx, @typescript-eslint/no-unsafe-member-access
								<p data-testid="home-recommended-action">Loading {message.messageInstanceId}</p>
							}
							extraAnalyticsAttributes={extraAnalyticsAttributes}
						/>
					))}
				</div>
			);
		},
		{
			choreographer: {
				isDefaultEnabled: false,
			},
		},
	),
);

export default HomeRecommendedActionPlacementComponent;
